import axios from 'axios';
import { getGlassDimensions, getRebateDimensions } from './frameDesigner';
/**
 * Validate the rim of a frame can be manufactured
 * @param sku SKU of the length
 * @param rebateSize Rebate size of the frame rim
 * @returns Validation result (promise)
 */
export async function validateFrame(sku, rebateSize) {
    const { data } = await axios
        .get('/api/validation/frame/manufacture', {
        params: {
            sku,
            rebateWidth: rebateSize.width,
            rebateLength: rebateSize.length
        }
    });
    return data;
}
/**
 * Validate a mount can be manufactured
 * @param sku SKU of the top mount
 * @param external External size of the mount
 * @param borders Borders
 * @param bottomSku SKU of the bottom mount
 * @returns Validation result (promise)
 */
export async function validateMount(sku, external, borders, bottomSku = null, revealSize = null) {
    const { data } = await axios.get('/api/validation/mountboard/manufacture', {
        params: {
            sku,
            width: external.width,
            length: external.length,
            borderTop: borders.top,
            borderBottom: borders.bottom,
            borderLeft: borders.left,
            borderRight: borders.right,
            bottomSku,
            revealSize
        }
    });
    return data;
}
/**
 * Validate that a sheet can be manufactured to a size
 * @param sku SKU of the sheet
 * @param size Size of the sheet
 * @returns Validation result (promise)
 */
export async function validateSheet(sku, size, inFrame = false) {
    const { data } = await axios.get('/api/validation/sheets/manufacture', {
        params: {
            sku,
            width: size.width,
            length: size.length,
            inFrame: inFrame ? 1 : 0
        }
    });
    return data;
}
/**
 * Validate a bespoke frame can be manufactured
 * @param item Bespoke frame item
 * @returns Validation result (promise)
 */
export async function validateBespokeFrame(item) {
    const promises = [];
    const result = {
        frame: { isValid: true, problems: [], warnings: [] },
        mount: { isValid: true, problems: [], warnings: [] },
        glazing: { isValid: true, problems: [], warnings: [] },
        backing: { isValid: true, problems: [], warnings: [] },
        undermount: { isValid: true, problems: [], warnings: [] },
        fastmount: { isValid: true, problems: [], warnings: [] }
    };
    const rebateDimensions = getRebateDimensions(item.size, item.mount.type != 'none' ? item.mount.borders : null);
    const glassDimensions = getGlassDimensions(rebateDimensions);
    if (item.moulding) {
        promises.push(validateFrame(item.moulding?.sku, rebateDimensions)
            .then(validation => result.frame = validation));
    }
    else {
        result.frame = { isValid: false, problems: ["You must select a frame moulding to order."], warnings: [] };
    }
    if (item.glazing) {
        promises.push(validateSheet(`${item.glazing.category?.name == 'Acrylic Glass' ? 'A' : ''}G-${item.glazing.id}`, glassDimensions, true).then(validation => result.glazing = validation));
    }
    else if (item.mount.type != 'none') {
        result.glazing = {
            isValid: false,
            problems: ["As you have chosen a mount we require that you choose a glazing option."],
            warnings: []
        };
    }
    if (item.mount.type != 'none') {
        promises.push(validateMount(`MCTS-${item.mount.top?.stock_lookup_sku}`, glassDimensions, item.mount.borders, item.mount.type == 'double' ? `MCTS-${item.mount.bottom?.stock_lookup_sku}` : null, item.mount.type == 'double' ? item.mount.reveal : null).then(validation => result.mount = validation));
    }
    if (!item.extras.backing && item.glazing != null) {
        result.backing = {
            isValid: false,
            problems: ["As you have chosen a glazing option we require that you have backing board."],
            warnings: []
        };
    }
    if (item.extras.undermount) {
        promises.push(validateSheet(`UM-61`, glassDimensions)
            .then(validation => result.undermount = validation));
    }
    if (item.extras.fastmount) {
        promises.push(validateSheet(`B-58`, glassDimensions)
            .then(validation => result.fastmount = validation));
    }
    await Promise.all(promises);
    return result;
}
